import React, { Suspense, useEffect, useRef } from 'react';
import {
    Button,
    VStack,
    Flex,
    Text,
    Grid,
    GridItem,
    Box,
    Center,
    List,
    ListItem,
} from '@chakra-ui/react';
import { CardSpinner } from '../../../domain';
import { fetchElements, getAllChildrenProps, getChildrenProps } from '../../util';
import { useLazySuspense, LazySuspense } from '../../vendor/lazy-suspense';
import { SettingsLayout as SettingsLayoutInterface } from './settingsLayout';
import { SettingsLayoutConfig } from './settingsLayoutConfig';
import { NavLink } from 'react-router-dom';

export function createSettingsLayout(
    config: SettingsLayoutConfig
): SettingsLayoutInterface {
    const _Wrapper: SettingsLayoutInterface['Wrapper'] = (props) => {
        return <>{props.children}</>;
    };
    const _Container: SettingsLayoutInterface['Container'] = (props) => {
        const { Body, NavigationList } = fetchElements(props.children, {
            // Header: _Header,
            Body: _Body,
            NavigationList: _NavigationList,
        });

        const { NavigationSection } = getAllChildrenProps(NavigationList.props.children, {
            NavigationSection: _NavigationSection,
        });

        // function getSectionItems() {
        //     return
        // }

        // const { NavigationItems } = getAllChildrenProps(NavigationList.props.children, {
        //     NavigationItems: _NavigationItem,
        // });

        const { elementRef, suspenseProps } = useLazySuspense();

        return (
            <Box
                h="full"
                maxW={{
                    md: '100vw',
                    lg: '92vw',
                    xl: '75vw',
                }}
                mx="auto"
                // py={8}
                pt="6vh"
            >
                <Grid
                    h="full"
                    templateAreas={`
                        "sidebar main"
                        "sidebar main"
                    `}
                    gridTemplateRows="min-content 1fr"
                    gridTemplateColumns="minmax(12vw, min-content) 1fr"
                    gap={{
                        md: 8,
                        lg: 12,
                        xl: 16,
                    }}
                >
                    <GridItem gridArea="sidebar">
                        <VStack
                            alignItems="flex-start"
                            fontWeight="medium"
                            color="whiteAlpha.900"
                            spacing={8}
                            w="full"
                        >
                            {NavigationSection.map((section, index) => (
                                <VStack
                                    key={index}
                                    alignItems="flex-start"
                                    spacing={2}
                                    w="full"
                                >
                                    <Box
                                        textTransform="uppercase"
                                        color="whiteAlpha.700"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        letterSpacing="wider"
                                        px={4}
                                    >
                                        {section.label}
                                    </Box>
                                    <VStack alignItems="flex-start" spacing={1} w="full">
                                        {getAllChildrenProps(section.children, {
                                            NavigationItems: _NavigationItem,
                                        })
                                            .NavigationItems.filter(
                                                (Item) => Item.visible !== false
                                            )
                                            .map((Item, index) => (
                                                <NavLink
                                                    style={{ width: '100%' }}
                                                    to={Item.to}
                                                    key={index}
                                                >
                                                    {({ isActive }) => (
                                                        <Box
                                                            borderStyle="solid"
                                                            borderStartWidth={3}
                                                            borderColor={
                                                                isActive
                                                                    ? 'green.500'
                                                                    : 'transparent'
                                                            }
                                                            w="full"
                                                            px={4}
                                                            py={2}
                                                            cursor="pointer"
                                                            aria-selected={isActive}
                                                            _selected={{
                                                                bg: 'whiteAlpha.200',
                                                            }}
                                                            _hover={{
                                                                bg: 'whiteAlpha.200',
                                                            }}
                                                            whiteSpace="nowrap"
                                                        >
                                                            {Item.children}
                                                        </Box>
                                                    )}
                                                </NavLink>
                                            ))}
                                    </VStack>
                                </VStack>
                            ))}
                        </VStack>
                    </GridItem>
                    <GridItem gridArea="main" position="relative" pb={32}>
                        <Suspense
                            {...suspenseProps}
                            fallback={
                                <Center
                                    position="relative"
                                    alignItems="center"
                                    h="full"
                                    py={4}
                                    top={-8}
                                >
                                    {/* {Loader} */}
                                    <CardSpinner />
                                </Center>
                            }
                        >
                            <Box ref={elementRef}>{Body}</Box>
                        </Suspense>
                    </GridItem>
                </Grid>
            </Box>
        );
    };
    const _Header: SettingsLayoutInterface['Header'] = (props) => {
        return <Box>{props.children}</Box>;
    };

    const _NavigationList: SettingsLayoutInterface['Navigation']['List'] = (props) => {
        return <Box>{props.children}</Box>;
    };

    const _NavigationItem: SettingsLayoutInterface['Navigation']['Item'] = (props) => {
        return <Box>{props.children}</Box>;
    };

    const _NavigationSection: SettingsLayoutInterface['Navigation']['Section'] = (
        props
    ) => {
        return <Box>{props.children}</Box>;
    };

    const _Body: SettingsLayoutInterface['Body'] = (props) => {
        return <Grid gap={8}>{props.children}</Grid>;
    };
    const _Section: SettingsLayoutInterface['Section'] = (props) => {
        return <GridItem>{props.children}</GridItem>;
    };

    const _Loader: SettingsLayoutInterface['Loader'] = (props) => {
        return <>{props.children}</>;
    };
    return {
        Wrapper: _Wrapper,
        Container: _Container,
        Navigation: {
            List: _NavigationList,
            Item: _NavigationItem,
            Section: _NavigationSection,
        },
        Header: _Header,
        Body: _Body,
        Section: _Section,
        Loader: _Loader,
    };
}
